import React, { useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Popover,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import EditSharpIcon from "@mui/icons-material/EditSharp";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import LockResetIcon from "@mui/icons-material/LockReset";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SaveIcon from "@mui/icons-material/Save";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import moment from "moment";
import { useGetDriverQuery, useCreateDriverMutation } from "../../../Api/attoDeskApi";
import { IDriver } from "../../../Api/Interface/api.interface";
import { useNotifier } from "../../../Core/Notifier";
import NewPopUpDriver from "../Edit/index";

// ── Helpers ─────────────────────────────────────────────────────────────────
const AVATAR_COLORS = ["#2f3891", "#2f3891", "#ec4899", "#f59e0b", "#10b981", "#3b82f6", "#ef4444"];

const avatarColor = (name: string) =>
  AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length];

const fmtTime = (t: string) =>
  t ? moment(t, "HH:mm").format("h:mm A") : "";

const fmtSalary = (s: string | number) =>
  s ? `$${Number(s).toFixed(2)}/hr` : "";

const DAY_ORDER = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

interface TimeSlot {
  startTime: string;
  endTime: string;
  hourlySalary: string;
}

// ── Shared field style ────────────────────────────────────────────────────────
const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    bgcolor: "#fff",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#a5b4fc" },
    "&.Mui-focused fieldset": { borderColor: "#2f3891", borderWidth: "1.5px" },
  },
  "& .MuiInputBase-input": { fontSize: 13 },
  "& .MuiFormHelperText-root": { fontSize: 11, mt: 0.4 },
};

const labelSx = {
  fontSize: 12,
  fontWeight: 600,
  color: "#475569",
  mb: 0.5,
  display: "block",
} as const;

// ── Sub-components ───────────────────────────────────────────────────────────
const DetailRow = ({
  label,
  value,
  icon,
}: {
  label: string;
  value?: string | null;
  icon?: React.ReactNode;
}) => (
  <Box sx={{ mb: 2.5 }}>
    <Typography
      variant="caption"
      sx={{
        color: "#94a3b8",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        fontSize: 10,
        display: "block",
        mb: 0.4,
      }}
    >
      {label}
    </Typography>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {icon && <Box sx={{ color: "#2f3891", display: "flex", flexShrink: 0 }}>{icon}</Box>}
      <Typography
        variant="body2"
        fontWeight={600}
        color={value ? "#0f172a" : "#cbd5e1"}
        sx={{ wordBreak: "break-word" }}
      >
        {value || "—"}
      </Typography>
    </Box>
  </Box>
);

const SectionCard = ({
  title,
  icon,
  children,
  action,
}: {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  action?: React.ReactNode;
}) => (
  <Paper
    elevation={0}
    sx={{
      border: "1px solid #e2e8f0",
      borderRadius: 3,
      overflow: "hidden",
      mb: 2.5,
      transition: "box-shadow 0.2s",
      "&:hover": { boxShadow: "0 4px 20px rgba(0,0,0,0.07)" },
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 3,
        py: 1.75,
        borderBottom: "1px solid #f1f5f9",
        bgcolor: "#fafafa",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {icon && <Box sx={{ color: "#2f3891", display: "flex" }}>{icon}</Box>}
        <Typography fontWeight={700} fontSize={13} color="#374151" textTransform="uppercase" letterSpacing="0.05em">
          {title}
        </Typography>
      </Box>
      {action}
    </Box>
    <Box sx={{ p: 3 }}>{children}</Box>
  </Paper>
);

// ── Quarter-hour time picker ─────────────────────────────────────────────────
// Single-click → popover (hours + :00 :15 :30 :45)
// Double-click → free-type text input
const QUARTER_MINUTES = ["00", "15", "30", "45"];
const ALL_HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));

const QuarterTimePicker = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) => {
  const [anchor,     setAnchor]     = useState<HTMLElement | null>(null);
  const [manualMode, setManualMode] = useState(false);
  const [manualVal,  setManualVal]  = useState(value);

  const parsedHour = value ? value.split(":")[0] : "";
  const parsedMin  = value ? value.split(":")[1] : "";
  const display    = value ? moment(value, "HH:mm").format("h:mm A") : "";

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (!manualMode) setAnchor(e.currentTarget);
  };

  const handleDoubleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setAnchor(null);
    setManualVal(value);
    setManualMode(true);
  };

  const handleManualBlur = () => {
    setManualMode(false);
  };

  const handleManualChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setManualVal(e.target.value);
    onChange(e.target.value);
  };

  const selectHour = (h: string) => {
    const m = parsedMin || "00";
    onChange(`${h}:${m}`);
  };

  const selectMinute = (m: string) => {
    const h = parsedHour || "00";
    onChange(`${h}:${m}`);
    setAnchor(null);
  };

  return (
    <Box>
      <Typography sx={labelSx}>{label}</Typography>

      {manualMode ? (
        <TextField
          autoFocus
          fullWidth
          size="small"
          type="time"
          value={manualVal}
          onChange={handleManualChange}
          onBlur={handleManualBlur}
          onClick={(e) => e.stopPropagation()}
          sx={fieldSx}
        />
      ) : (
        <Box
          onClick={handleClick}
          onDoubleClick={handleDoubleClick}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            border: "1px solid",
            borderColor: Boolean(anchor) ? "#2f3891" : "#e2e8f0",
            borderWidth: Boolean(anchor) ? "1.5px" : "1px",
            borderRadius: "8px",
            px: 1.5,
            height: 37,
            cursor: "pointer",
            bgcolor: "#fff",
            userSelect: "none",
            transition: "border-color 0.15s",
            "&:hover": { borderColor: "#a5b4fc" },
          }}
        >
          <Typography fontSize={13} color={value ? "#0f172a" : "#94a3b8"}>
            {display || "Select time"}
          </Typography>
          <AccessTimeOutlinedIcon sx={{ fontSize: 16, color: "#94a3b8", flexShrink: 0 }} />
        </Box>
      )}

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0 8px 30px rgba(0,0,0,0.13)",
            mt: 0.5,
            overflow: "hidden",
          },
        }}
      >
        <Box sx={{ display: "flex" }}>
          {/* Hours */}
          <Box sx={{ width: 72, maxHeight: 220, overflowY: "auto", borderRight: "1px solid #f1f5f9" }}>
            <Box sx={{ px: 1.5, pt: 1, pb: 0.5, position: "sticky", top: 0, bgcolor: "#fff", zIndex: 1 }}>
              <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 700, fontSize: 10, letterSpacing: "0.06em" }}>
                HOUR
              </Typography>
            </Box>
            {ALL_HOURS.map((h) => {
              const isSelected = h === parsedHour;
              return (
                <Box
                  key={h}
                  onClick={() => selectHour(h)}
                  sx={{
                    px: 1.5, py: 0.65,
                    cursor: "pointer",
                    fontSize: 13,
                    fontWeight: isSelected ? 700 : 400,
                    bgcolor: isSelected ? "#ede9fe" : "transparent",
                    color: isSelected ? "#2f3891" : "#374151",
                    "&:hover": { bgcolor: isSelected ? "#ede9fe" : "#f5f3ff" },
                  }}
                >
                  {moment(h, "HH").format("h A")}
                </Box>
              );
            })}
          </Box>

          {/* Minutes */}
          <Box sx={{ width: 72 }}>
            <Box sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
              <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 700, fontSize: 10, letterSpacing: "0.06em" }}>
                MIN
              </Typography>
            </Box>
            {QUARTER_MINUTES.map((m) => {
              const isSelected = m === parsedMin;
              return (
                <Box
                  key={m}
                  onClick={() => selectMinute(m)}
                  sx={{
                    px: 1.5, py: 0.9,
                    cursor: "pointer",
                    fontSize: 13,
                    fontWeight: isSelected ? 700 : 400,
                    bgcolor: isSelected ? "#ede9fe" : "transparent",
                    color: isSelected ? "#2f3891" : "#374151",
                    "&:hover": { bgcolor: isSelected ? "#ede9fe" : "#f5f3ff" },
                  }}
                >
                  :{m}
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Hint */}
        <Box sx={{ px: 2, py: 1, borderTop: "1px solid #f1f5f9", bgcolor: "#fafafa" }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
            Double-click to type manually
          </Typography>
        </Box>
      </Popover>
    </Box>
  );
};

// ── Main Component ───────────────────────────────────────────────────────────
const DriverView = () => {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const { id: customerId } = useParams<{ id: string }>();
  const { showErrorMessage, showMessage } = useNotifier();

  // ── Edit dialog state ──────────────────────────────────────────────────────
  const [openDialog,     setOpenDialog]     = useState(false);
  const [selectCustomer, setSelectCustomer] = useState<IDriver | null>(null);

  // ── Reset password modal state ─────────────────────────────────────────────
  const [pwdOpen,    setPwdOpen]    = useState(false);
  const [newPwd,     setNewPwd]     = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);
  const [pwdSaving,  setPwdSaving]  = useState(false);
  const [pwdErrors,  setPwdErrors]  = useState<{ newPwd?: string; confirmPwd?: string }>({});

  // ── Availability inline edit state ─────────────────────────────────────────
  const [expandedDay,  setExpandedDay]  = useState<string | null>(null);
  const [editSlots,    setEditSlots]    = useState<TimeSlot[]>([]);
  const [availSaving,  setAvailSaving]  = useState(false);

  // ── "Apply to all days" template state ─────────────────────────────────────
  // Lets the admin set one start/end/rate and broadcast it to all 7 days
  // in a single save instead of editing each day individually.
  const [bulkStart, setBulkStart] = useState<string>("");
  const [bulkEnd,   setBulkEnd]   = useState<string>("");
  const [bulkRate,  setBulkRate]  = useState<string>("");
  const [bulkSaving, setBulkSaving] = useState(false);

  // ── API ───────────────────────────────────────────────────────────────────
  const { data, isLoading, isError } = useGetDriverQuery({
    searchText: "",
    id: String(customerId),
  });
  const [updateDriver] = useCreateDriverMutation();

  const customer = useMemo<IDriver | null>(() => {
    if (data && Array.isArray(data.data) && data.data.length > 0) return data.data[0] as IDriver;
    return null;
  }, [data]);

  // Trips assigned to this driver — backend attaches them to the driver row
  // returned by the GET driver endpoint when an `id` is provided.
  const assignedTrips: any[] = useMemo(() => {
    const c: any = customer;
    return Array.isArray(c?.trips) ? c.trips : [];
  }, [customer]);

  // Debug: helps tell apart "backend returned nothing" vs "UI did not render"
  // when triaging a missing-trips report.
  // eslint-disable-next-line no-console
  React.useEffect(() => { console.log("DRIVER TRIPS:", assignedTrips); }, [assignedTrips]);

  // ── Edit profile handlers ──────────────────────────────────────────────────
  const handleOpenEdit = () => {
    setSelectCustomer(customer);
    setOpenDialog(true);
  };
  const handleCloseDialog = () => {
    setSelectCustomer(null);
    setOpenDialog(false);
  };

  // ── Reset password handlers ────────────────────────────────────────────────
  const handleOpenPwdModal = () => {
    setNewPwd(""); setConfirmPwd(""); setPwdErrors({}); setShowNewPwd(false);
    setPwdOpen(true);
  };
  const handleClosePwdModal = () => setPwdOpen(false);
  const handleSavePassword = async () => {
    const errs: { newPwd?: string; confirmPwd?: string } = {};
    if (!newPwd)           errs.newPwd     = "New password is required";
    if (!confirmPwd)       errs.confirmPwd = "Please confirm your password";
    else if (newPwd !== confirmPwd) errs.confirmPwd = "Passwords do not match";
    setPwdErrors(errs);
    if (Object.keys(errs).length) return;
    if (!customer) return;
    setPwdSaving(true);
    try {
      const res = await updateDriver({
        id:               customer.id,
        driverFirstName:  customer.driverFirstName,
        driverLastName:   customer.driverLastName,
        driverMobileNum:  customer.driverMobileNum,
        driverAltMobileNum: customer.driverAltMobileNum,
        driverEmail:      customer.driverEmail,
        driverCity:       customer.driverCity,
        driverAddress:    customer.driverAddress,
        driverUserRole:   customer.driverUserRole,
        driverUserName:   customer.driverUserName,
        driverPassWord:   newPwd,
        driverDescription: customer.driverDescription,
        driverImg:        customer.driverImg,
        availability:     customer.availability,
      }).unwrap();
      if (!res.status) showErrorMessage(res.message);
      else { showMessage("Password updated successfully"); handleClosePwdModal(); }
    } catch { showErrorMessage("Something went wrong"); }
    finally { setPwdSaving(false); }
  };

  // ── Availability inline edit handlers ─────────────────────────────────────
  const handleExpandDay = (day: string, slots: any[]) => {
    if (expandedDay === day) { setExpandedDay(null); return; }
    setExpandedDay(day);
    setEditSlots(
      (slots || []).length > 0
        ? slots.map((s: any) => ({
            startTime:    s.startTime    || "",
            endTime:      s.endTime      || "",
            hourlySalary: String(s.hourlySalary ?? ""),
          }))
        : [{ startTime: "", endTime: "", hourlySalary: "" }]
    );
  };
  const handleCancelDay = () => setExpandedDay(null);
  const handleSlotChange = (idx: number, field: keyof TimeSlot, value: string) => {
    setEditSlots((prev) => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };
  const handleAddSlot = () =>
    setEditSlots((prev) => [...prev, { startTime: "", endTime: "", hourlySalary: "" }]);
  const handleRemoveSlot = (idx: number) =>
    setEditSlots((prev) => prev.filter((_, i) => i !== idx));
  const handleSaveDay = async () => {
    if (!expandedDay || !customer) return;
    setAvailSaving(true);
    try {
      const newAvail = (customer.availability || []).map((a: any) =>
        a.day === expandedDay ? { ...a, timeSlots: editSlots } : a
      );
      const res = await updateDriver({
        id:               customer.id,
        driverFirstName:  customer.driverFirstName,
        driverLastName:   customer.driverLastName,
        driverMobileNum:  customer.driverMobileNum,
        driverAltMobileNum: customer.driverAltMobileNum,
        driverEmail:      customer.driverEmail,
        driverCity:       customer.driverCity,
        driverAddress:    customer.driverAddress,
        driverUserRole:   customer.driverUserRole,
        driverUserName:   customer.driverUserName,
        driverPassWord:   customer.driverPassWord,
        driverDescription: customer.driverDescription,
        driverImg:        customer.driverImg,
        availability:     newAvail,
      }).unwrap();
      if (!res.status) showErrorMessage(res.message);
      else { showMessage("Availability updated"); setExpandedDay(null); }
    } catch { showErrorMessage("Something went wrong"); }
    finally { setAvailSaving(false); }
  };

  // Broadcast the bulk template (start/end/rate) to every day in DAY_ORDER,
  // overwriting whatever each day previously had with one identical slot.
  const handleApplyToAllDays = async () => {
    if (!customer) return;
    if (!bulkStart || !bulkEnd) {
      showErrorMessage("Set both start and end time before applying to all days.");
      return;
    }
    if (!bulkRate || Number(bulkRate) <= 0) {
      showErrorMessage("Set an hourly rate greater than 0.");
      return;
    }
    setBulkSaving(true);
    try {
      const slot: TimeSlot = { startTime: bulkStart, endTime: bulkEnd, hourlySalary: bulkRate };
      const newAvail = DAY_ORDER.map((day) => ({ day, timeSlots: [{ ...slot }] }));
      const res = await updateDriver({
        id:                 customer.id,
        driverFirstName:    customer.driverFirstName,
        driverLastName:     customer.driverLastName,
        driverMobileNum:    customer.driverMobileNum,
        driverAltMobileNum: customer.driverAltMobileNum,
        driverEmail:        customer.driverEmail,
        driverCity:         customer.driverCity,
        driverAddress:      customer.driverAddress,
        driverUserRole:     customer.driverUserRole,
        driverUserName:     customer.driverUserName,
        driverPassWord:     customer.driverPassWord,
        driverDescription:  customer.driverDescription,
        driverImg:          customer.driverImg,
        availability:       newAvail,
      }).unwrap();
      if (!res.status) showErrorMessage(res.message);
      else {
        showMessage("Availability applied to all days");
        setExpandedDay(null);
      }
    } catch { showErrorMessage("Something went wrong"); }
    finally { setBulkSaving(false); }
  };

  // Always return all 7 days, in canonical order. Days that have no
  // availability record on the driver's profile come back with an empty
  // timeSlots array so the UI can render a "Not Available" placeholder
  // and an Add button instead of skipping the row entirely.
  const sortedAvailability = useMemo(() => {
    const existing = Array.isArray(customer?.availability)
      ? (customer!.availability as any[])
      : [];
    const byDay: Record<string, any> = {};
    for (const a of existing) {
      if (a && typeof a === "object" && a.day) byDay[a.day] = a;
    }
    return DAY_ORDER.map((day) => {
      const found = byDay[day];
      return found
        ? { ...found, timeSlots: Array.isArray(found.timeSlots) ? found.timeSlots : [] }
        : { day, timeSlots: [] };
    });
  }, [customer]);

  // ── Loading / error states ─────────────────────────────────────────────────
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
        <CircularProgress sx={{ color: "#2f3891" }} />
      </Box>
    );
  }

  if (isError || !customer) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
        <Typography color="text.secondary">Unable to load driver profile.</Typography>
      </Box>
    );
  }

  const fullName  = `${customer.driverFirstName} ${customer.driverLastName}`;
  const initials  = `${customer.driverFirstName?.[0] ?? ""}${customer.driverLastName?.[0] ?? ""}`.toUpperCase();
  const roleLabel = customer.driverUserRole === "driver" ? "Driver" : customer.driverUserRole ?? "User";

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: "#f9fafb", minHeight: "100vh" }}>

      {/* ── Page header ── */}
      <Paper
        elevation={0}
        sx={{
          border: "1px solid #e2e8f0",
          borderRadius: 3,
          p: 3,
          mb: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        {/* Left: avatar + name */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
          <Avatar
            sx={{
              width: 64,
              height: 64,
              bgcolor: avatarColor(customer.driverFirstName ?? "D"),
              fontSize: 22,
              fontWeight: 700,
            }}
          >
            {initials || "D"}
          </Avatar>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
              <Typography variant="h5" fontWeight={800} color="#0f172a" lineHeight={1.2}>
                {fullName}
              </Typography>
              <Chip
                label={roleLabel}
                size="small"
                sx={{
                  bgcolor: "#ede9fe",
                  color: "#2f3891",
                  fontWeight: 700,
                  fontSize: 11,
                  height: 22,
                  textTransform: "capitalize",
                }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" mt={0.4}>
              Driver Profile · ID #{customer.id}
            </Typography>
          </Box>
        </Box>

        {/* Right: action buttons */}
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIosIcon fontSize="small" />}
            onClick={() => navigate(-1)}
            sx={{ textTransform: "none", borderRadius: 2, borderColor: "#e2e8f0", color: "#64748b" }}
          >
            Back
          </Button>
          <Button
            variant="outlined"
            startIcon={<DirectionsBusIcon fontSize="small" />}
            onClick={() => navigate("/driverBooking/list")}
            sx={{ textTransform: "none", borderRadius: 2, borderColor: "#2f3891", color: "#2f3891" }}
          >
            Assign Trip
          </Button>
          <Button
            variant="contained"
            startIcon={<EditSharpIcon fontSize="small" />}
            onClick={handleOpenEdit}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              bgcolor: "#2f3891",
              "&:hover": { bgcolor: "#2f3891" },
            }}
          >
            Edit Profile
          </Button>
        </Box>
      </Paper>

      {/* ── Body: 2-column layout ── */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "320px 1fr",
          gap: 3,
          alignItems: "start",
        }}
      >
        {/* ══ LEFT COLUMN ══════════════════════════════════════════════════════ */}
        <Box>

          {/* Contact Info */}
          <SectionCard
            title="Contact Information"
            icon={<PersonOutlinedIcon sx={{ fontSize: 18 }} />}
            action={
              <Tooltip title="Edit Profile">
                <Box
                  onClick={handleOpenEdit}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    cursor: "pointer",
                    color: "#2f3891",
                    fontSize: 12,
                    fontWeight: 600,
                    "&:hover": { opacity: 0.8 },
                  }}
                >
                  <EditSharpIcon sx={{ fontSize: 14 }} />
                  Edit
                </Box>
              </Tooltip>
            }
          >
            <DetailRow
              label="Phone"
              value={customer.driverMobileNum}
              icon={<PhoneOutlinedIcon sx={{ fontSize: 16 }} />}
            />
            <DetailRow
              label="Alternate Phone"
              value={customer.driverAltMobileNum}
              icon={<PhoneOutlinedIcon sx={{ fontSize: 16 }} />}
            />
            <DetailRow
              label="Email"
              value={customer.driverEmail}
              icon={<EmailOutlinedIcon sx={{ fontSize: 16 }} />}
            />
            <DetailRow
              label="City"
              value={customer.driverCity}
              icon={<ApartmentOutlinedIcon sx={{ fontSize: 16 }} />}
            />
            <DetailRow
              label="Address"
              value={customer.driverAddress}
              icon={<LocationOnOutlinedIcon sx={{ fontSize: 16 }} />}
            />
          </SectionCard>

          {/* Account */}
          <SectionCard
            title="Account"
            icon={<BadgeOutlinedIcon sx={{ fontSize: 18 }} />}
          >
            <DetailRow
              label="Username"
              value={customer.driverUserName}
              icon={<PersonOutlinedIcon sx={{ fontSize: 16 }} />}
            />
            <Divider sx={{ my: 2 }} />
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: "#94a3b8",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  fontSize: 10,
                  display: "block",
                  mb: 1,
                }}
              >
                Password
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<LockResetIcon fontSize="small" />}
                onClick={handleOpenPwdModal}
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  borderColor: "#e2e8f0",
                  color: "#64748b",
                  fontSize: 12,
                  "&:hover": { borderColor: "#2f3891", color: "#2f3891" },
                }}
              >
                Reset Password
              </Button>
            </Box>
          </SectionCard>

          {/* Notes / Description */}
          <SectionCard
            title="Notes"
            icon={<NotesOutlinedIcon sx={{ fontSize: 18 }} />}
          >
            {customer.driverDescription ? (
              <Typography
                variant="body2"
                color="#374151"
                sx={{ lineHeight: 1.75, whiteSpace: "pre-wrap" }}
              >
                {customer.driverDescription}
              </Typography>
            ) : (
              <Typography variant="body2" color="#cbd5e1" fontStyle="italic">
                No notes added
              </Typography>
            )}
          </SectionCard>
        </Box>

        {/* ══ RIGHT COLUMN ═════════════════════════════════════════════════════ */}
        <Box>
          {/* ── Assigned Trips ─────────────────────────────────────────── */}
          <SectionCard
            title="Assigned Trips"
            icon={<DirectionsBusIcon sx={{ fontSize: 18 }} />}
          >
            {assignedTrips.length === 0 ? (
              <Box
                sx={{
                  textAlign: "center",
                  py: 6,
                  border: "2px dashed #e2e8f0",
                  borderRadius: 2,
                  color: "#94a3b8",
                }}
              >
                <DirectionsBusIcon sx={{ fontSize: 36, mb: 1, opacity: 0.5 }} />
                <Typography variant="body2" sx={{ textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 700 }}>
                  No Trips Assigned
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {assignedTrips.map((trip: any) => {
                  const pickupDt = trip.pickupDateTime ? dayjs(trip.pickupDateTime) : null;
                  const dateStr  = pickupDt && pickupDt.isValid() ? pickupDt.format("MMM D, YYYY h:mm A") : "—";
                  const route    = `${trip.from || "—"} → ${trip.to || "—"}`;
                  const isInv    = !!trip.isInvoice;
                  return (
                    <Box
                      key={trip.id}
                      onClick={() => navigate(`/work-order/history/${trip.workOrderId}`)}
                      sx={{
                        cursor: "pointer",
                        borderRadius: 2,
                        border: "1px solid #e2e8f0",
                        bgcolor: "#fff",
                        p: 1.75,
                        transition: "border-color 0.15s, box-shadow 0.15s",
                        "&:hover": { borderColor: "#a5b4fc", boxShadow: "0 2px 8px rgba(99,102,241,0.08)" },
                      }}
                    >
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5, flexWrap: "wrap", gap: 1 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>{dateStr}</Typography>
                        <Box sx={{ display: "flex", gap: 0.75, alignItems: "center" }}>
                          {trip.invoiceNo && (
                            <Box sx={{ px: 1, py: 0.25, borderRadius: 1, bgcolor: "#f0fdf4", border: "1px solid #bbf7d0" }}>
                              <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "#15803d" }}>{trip.invoiceNo}</Typography>
                            </Box>
                          )}
                          {!trip.invoiceNo && trip.workOrderNo && (
                            <Box sx={{ px: 1, py: 0.25, borderRadius: 1, bgcolor: "#eef2ff", border: "1px solid #c7d2fe" }}>
                              <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "#4338ca" }}>{trip.workOrderNo}</Typography>
                            </Box>
                          )}
                          <Box sx={{ px: 1, py: 0.25, borderRadius: 1, bgcolor: isInv ? "#dcfce7" : "#fef3c7", border: `1px solid ${isInv ? "#86efac" : "#fde68a"}` }}>
                            <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: isInv ? "#15803d" : "#b45309" }}>
                              {trip.status || (isInv ? "Invoice" : "Quote")}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                      <Typography sx={{ fontSize: 12, color: "#475569", mb: 0.25 }}>{route}</Typography>
                      <Typography sx={{ fontSize: 11.5, color: "#64748b" }}>
                        Client: <Box component="span" sx={{ color: "#1e293b", fontWeight: 600 }}>{trip.clientName || "—"}</Box>
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}
          </SectionCard>

          <SectionCard
            title="Weekly Availability"
            icon={<AccessTimeOutlinedIcon sx={{ fontSize: 18 }} />}
            action={
              <Button
                size="small"
                startIcon={<EditSharpIcon fontSize="small" />}
                onClick={handleOpenEdit}
                sx={{
                  textTransform: "none",
                  fontSize: 12,
                  color: "#2f3891",
                  fontWeight: 600,
                  "&:hover": { bgcolor: "#ede9fe" },
                }}
              >
                Edit Schedule
              </Button>
            }
          >
            {/* ── Apply-to-all-days template ───────────────────────────── */}
            <Box
              sx={{
                mb: 2,
                p: 1.75,
                borderRadius: 2,
                border: "1px dashed #c7d2fe",
                bgcolor: "#f5f3ff",
              }}
            >
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#4338ca", textTransform: "uppercase", letterSpacing: 0.5, mb: 1 }}>
                Apply to all 7 days
              </Typography>
              <Grid container spacing={1.5} alignItems="flex-end">
                <Grid item xs={12} sm={4}>
                  <QuarterTimePicker
                    label="Start Time"
                    value={bulkStart}
                    onChange={(v) => setBulkStart(v)}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <QuarterTimePicker
                    label="End Time"
                    value={bulkEnd}
                    onChange={(v) => setBulkEnd(v)}
                  />
                </Grid>
                <Grid item xs={8} sm={2}>
                  <Typography sx={labelSx}>Hourly Rate ($)</Typography>
                  <TextField
                    fullWidth size="small" type="number" placeholder="0.00"
                    value={bulkRate}
                    onChange={(e) => setBulkRate(e.target.value)}
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={4} sm={2}>
                  <Button
                    fullWidth
                    variant="contained"
                    disabled={bulkSaving}
                    onClick={handleApplyToAllDays}
                    sx={{
                      textTransform: "none", borderRadius: 2, fontWeight: 700, fontSize: 12,
                      bgcolor: "#2f3891", "&:hover": { bgcolor: "#252d73" },
                    }}
                  >
                    {bulkSaving ? "Applying…" : "Apply to All"}
                  </Button>
                </Grid>
              </Grid>
              <Typography sx={{ fontSize: 10.5, color: "#64748b", mt: 1 }}>
                Replaces any existing slots on every day with the values above.
              </Typography>
            </Box>

            {sortedAvailability.length === 0 ? (
              <Box
                sx={{
                  textAlign: "center",
                  py: 6,
                  border: "2px dashed #e2e8f0",
                  borderRadius: 2,
                  color: "#94a3b8",
                }}
              >
                <AccessTimeOutlinedIcon sx={{ fontSize: 36, mb: 1, opacity: 0.5 }} />
                <Typography variant="body2">No availability schedule set</Typography>
                <Button
                  size="small"
                  onClick={handleOpenEdit}
                  sx={{ textTransform: "none", mt: 1.5, color: "#2f3891", fontWeight: 600 }}
                >
                  + Set Availability
                </Button>
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {sortedAvailability.map((avail) => {
                  const hasSlots = avail.timeSlots && avail.timeSlots.length > 0;
                  const validSlots = hasSlots
                    ? avail.timeSlots.filter(
                        (s: any) => s.startTime && s.endTime && Number(s.hourlySalary) > 0
                      )
                    : [];
                  const isAvailable  = validSlots.length > 0;
                  const isExpanded   = expandedDay === avail.day;
                  const isSavingThis = availSaving && isExpanded;

                  return (
                    <Box
                      key={avail.day}
                      sx={{
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: isExpanded ? "#a5b4fc" : isAvailable ? "#e0e7ff" : "#f1f5f9",
                        bgcolor: isExpanded ? "#f5f3ff" : isAvailable ? "#fafafa" : "#f8fafc",
                        overflow: "hidden",
                        transition: "border-color 0.15s, background-color 0.15s, box-shadow 0.15s",
                        "&:hover": { borderColor: "#a5b4fc", boxShadow: "0 2px 8px rgba(99,102,241,0.08)" },
                      }}
                    >
                      {/* ── Clickable row header ── */}
                      <Box
                        onClick={() => handleExpandDay(avail.day, avail.timeSlots || [])}
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 2,
                          p: 2,
                          cursor: "pointer",
                        }}
                      >
                        {/* Day name */}
                        <Box sx={{ minWidth: 96 }}>
                          <Typography
                            fontWeight={700}
                            fontSize={13}
                            color={isAvailable ? "#0f172a" : "#94a3b8"}
                          >
                            {avail.day}
                          </Typography>
                        </Box>

                        {/* Slots summary */}
                        <Box sx={{ flex: 1 }}>
                          {isAvailable ? (
                            validSlots.map((slot: any, si: number) => (
                              <Box
                                key={si}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  flexWrap: "wrap",
                                  gap: 1.5,
                                  mb: si < validSlots.length - 1 ? 0.75 : 0,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex", alignItems: "center", gap: 0.75,
                                    bgcolor: "#ede9fe", borderRadius: 1.5, px: 1.5, py: 0.5,
                                  }}
                                >
                                  <AccessTimeOutlinedIcon sx={{ fontSize: 13, color: "#2f3891" }} />
                                  <Typography fontSize={13} fontWeight={600} color="#2f3891">
                                    {fmtTime(slot.startTime)} – {fmtTime(slot.endTime)}
                                  </Typography>
                                </Box>
                                <Box
                                  sx={{
                                    display: "flex", alignItems: "center", gap: 0.5,
                                    bgcolor: "#dcfce7", borderRadius: 1.5, px: 1.25, py: 0.5,
                                  }}
                                >
                                  <Typography fontSize={12} fontWeight={700} color="#16a34a">
                                    {fmtSalary(slot.hourlySalary)}
                                  </Typography>
                                </Box>
                              </Box>
                            ))
                          ) : (
                            <Chip
                              label="Not Available"
                              size="small"
                              sx={{ bgcolor: "#f1f5f9", color: "#94a3b8", fontSize: 11, fontWeight: 600, height: 22 }}
                            />
                          )}
                        </Box>

                        {/* Expand arrow + edit/add hint */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                          <Typography sx={{ fontSize: 11, color: "#a5b4fc", fontWeight: 600 }}>
                            {isExpanded ? "Close" : isAvailable ? "Edit" : "Add"}
                          </Typography>
                          <Box
                            sx={{
                              color: "#2f3891",
                              display: "flex",
                              transform: isExpanded ? "rotate(180deg)" : "none",
                              transition: "transform 0.2s",
                            }}
                          >
                            <ExpandMoreIcon sx={{ fontSize: 20 }} />
                          </Box>
                        </Box>
                      </Box>

                      {/* ── Inline slot editor ── */}
                      <Collapse in={isExpanded}>
                        <Divider sx={{ borderColor: "#e2e8f0" }} />
                        <Box sx={{ p: 2, bgcolor: "#fff" }}>
                          {editSlots.map((slot, idx) => (
                            <Box
                              key={idx}
                              sx={{
                                display: "flex", gap: 1.5, alignItems: "flex-end",
                                mb: 1.5, bgcolor: "#f8fafc", borderRadius: 2,
                                p: 1.75, border: "1px solid #f1f5f9",
                              }}
                            >
                              <Grid container spacing={1.5} alignItems="flex-end">
                                <Grid item xs={12} sm={4}>
                                  <QuarterTimePicker
                                    label="Start Time"
                                    value={slot.startTime}
                                    onChange={(v) => handleSlotChange(idx, "startTime", v)}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                  <QuarterTimePicker
                                    label="End Time"
                                    value={slot.endTime}
                                    onChange={(v) => handleSlotChange(idx, "endTime", v)}
                                  />
                                </Grid>
                                <Grid item xs={10} sm={3}>
                                  <Typography sx={labelSx}>Hourly Rate ($)</Typography>
                                  <TextField
                                    fullWidth size="small" type="number" placeholder="0.00"
                                    value={slot.hourlySalary}
                                    onChange={(e) => handleSlotChange(idx, "hourlySalary", e.target.value)}
                                    sx={fieldSx}
                                  />
                                </Grid>
                                <Grid item xs={2} sm={1}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRemoveSlot(idx)}
                                    disabled={editSlots.length === 1}
                                    sx={{ color: "#dc2626", borderRadius: 1.5, "&:hover": { bgcolor: "#fef2f2" } }}
                                  >
                                    <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Grid>
                              </Grid>
                            </Box>
                          ))}

                          {/* Add slot */}
                          <Button
                            size="small"
                            startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                            onClick={handleAddSlot}
                            sx={{
                              textTransform: "none", fontSize: 12, fontWeight: 700,
                              color: "#2f3891", borderRadius: 2, px: 1.5, py: 0.6,
                              border: "1.5px dashed #c7d2fe", bgcolor: "#f5f3ff",
                              mb: 2,
                              "&:hover": { bgcolor: "#eef2ff", borderColor: "#2f3891" },
                            }}
                          >
                            Add Time Slot
                          </Button>

                          {/* Save / Cancel */}
                          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                            <Button
                              size="small"
                              onClick={handleCancelDay}
                              sx={{
                                textTransform: "none", fontSize: 12.5, fontWeight: 600,
                                borderRadius: 2, borderColor: "#e2e8f0", color: "#64748b",
                                border: "1px solid #e2e8f0",
                                "&:hover": { borderColor: "#94a3b8", bgcolor: "#f8fafc" },
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={isSavingThis ? <CircularProgress size={11} color="inherit" /> : <SaveIcon sx={{ fontSize: 14 }} />}
                              onClick={handleSaveDay}
                              disabled={isSavingThis}
                              sx={{
                                textTransform: "none", fontSize: 12.5, fontWeight: 700,
                                borderRadius: 2, bgcolor: "#2f3891", boxShadow: "none",
                                "&:hover": { bgcolor: "#2f3891" },
                              }}
                            >
                              {isSavingThis ? "Saving…" : "Save"}
                            </Button>
                          </Box>
                        </Box>
                      </Collapse>
                    </Box>
                  );
                })}
              </Box>
            )}
          </SectionCard>
        </Box>
      </Box>

      {/* ── Edit Profile dialog ── */}
      {selectCustomer && (
        <NewPopUpDriver
          openModel={openDialog}
          handleCloseDialog={handleCloseDialog}
          data={selectCustomer}
        />
      )}

      {/* ── Reset Password modal ── */}
      <Dialog
        open={pwdOpen}
        onClose={handleClosePwdModal}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, boxShadow: "0 20px 60px rgba(0,0,0,0.15)" } }}
      >
        <DialogTitle
          sx={{
            fontWeight: 800, fontSize: 17, color: "#0f172a",
            pb: 0.5, display: "flex", alignItems: "center", gap: 1,
          }}
        >
          <LockResetIcon sx={{ color: "#2f3891", fontSize: 22 }} />
          Reset Password
        </DialogTitle>
        <DialogContent sx={{ pt: "12px !important" }}>
          <Typography sx={{ fontSize: 13, color: "#64748b", mb: 2.5 }}>
            Set a new password for <strong>{fullName}</strong>.
          </Typography>

          <Typography sx={labelSx}>New Password *</Typography>
          <TextField
            fullWidth size="small"
            type={showNewPwd ? "text" : "password"}
            placeholder="Enter new password"
            value={newPwd}
            onChange={(e) => { setNewPwd(e.target.value); setPwdErrors((p) => ({ ...p, newPwd: undefined })); }}
            error={Boolean(pwdErrors.newPwd)}
            helperText={pwdErrors.newPwd}
            sx={{ ...fieldSx, mb: 2 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowNewPwd((v) => !v)} edge="end">
                    {showNewPwd
                      ? <Visibility sx={{ fontSize: 18 }} />
                      : <VisibilityOff sx={{ fontSize: 18 }} />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Typography sx={labelSx}>Confirm Password *</Typography>
          <TextField
            fullWidth size="small"
            type={showConfirmPwd ? "text" : "password"}
            placeholder="Re-enter new password"
            value={confirmPwd}
            onChange={(e) => { setConfirmPwd(e.target.value); setPwdErrors((p) => ({ ...p, confirmPwd: undefined })); }}
            error={Boolean(pwdErrors.confirmPwd)}
            helperText={pwdErrors.confirmPwd}
            sx={fieldSx}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" aria-label={showConfirmPwd ? "Hide password" : "Show password"} onClick={() => setShowConfirmPwd((v) => !v)} edge="end">
                    {showConfirmPwd
                      ? <Visibility sx={{ fontSize: 18 }} />
                      : <VisibilityOff sx={{ fontSize: 18 }} />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={handleClosePwdModal}
            sx={{
              textTransform: "none", fontWeight: 600, fontSize: 13,
              borderRadius: 2, color: "#64748b",
              border: "1px solid #e2e8f0",
              "&:hover": { borderColor: "#94a3b8", bgcolor: "#f8fafc" },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSavePassword}
            disabled={pwdSaving}
            startIcon={pwdSaving ? <CircularProgress size={12} color="inherit" /> : <SaveIcon sx={{ fontSize: 14 }} />}
            sx={{
              textTransform: "none", fontWeight: 700, fontSize: 13,
              borderRadius: 2, bgcolor: "#2f3891", boxShadow: "none",
              "&:hover": { bgcolor: "#2f3891" },
            }}
          >
            {pwdSaving ? "Updating…" : "Update Password"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DriverView;
